import {
  CopyIconButton,
  Tag,
  Text,
  Tooltip,
  TruncatedContent,
} from "@aurem/ui";
import { FormattedDate } from "components/display/FormattedDate";
import { UUID } from "components/display/UUID";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { FieldType } from "./types";

type FieldRendererProps = {
  value: unknown;
  item: unknown;
  enumValues?: Record<string, { label: string; color?: string }>;
};

const renderString = ({ value }: FieldRendererProps): ReactNode => {
  if (value === null || value === undefined) return "-";
  return <TruncatedContent>{String(value)}</TruncatedContent>;
};

const renderDateTime = ({ value }: FieldRendererProps): ReactNode => {
  if (!value) return "-";
  return <FormattedDate value={value as string | Date} withTime />;
};

const renderEnum = ({ value, enumValues }: FieldRendererProps): ReactNode => {
  if (!value) return "-";
  const enumValue = String(value);
  const config = enumValues?.[enumValue];

  if (!config) {
    return <Tag color="neutral" label={enumValue} />;
  }

  const color = config.color || "neutral";
  return (
    <Tag
      color={color as "neutral" | "primary" | "success" | "warning" | "danger"}
      label={config.label}
    />
  );
};

const renderJSON = ({ value }: FieldRendererProps): ReactNode => {
  if (!value) return "-";

  try {
    const jsonString =
      typeof value === "string" ? value : JSON.stringify(value, null, 2);

    return (
      <Tooltip
        content={
          <pre className="text-xs max-h-96 overflow-auto">{jsonString}</pre>
        }
      >
        <div className="inline-flex items-center gap-1 group">
          <code className="text-xs font-mono cursor-help">
            {"{"} ... {"}"}
          </code>
          <CopyIconButton
            copyContent={jsonString}
            size="xs"
            variant="ghost"
            className="opacity-0 group-hover:opacity-100"
          />
        </div>
      </Tooltip>
    );
  } catch {
    return String(value);
  }
};

const renderUser = ({ value, item }: FieldRendererProps): ReactNode => {
  const id = String(value ?? "");
  if (!id || id === "null" || id === "undefined") return "-";

  const itemWithUser = item as {
    user?: { id: string; profile?: { name?: string } };
  };
  const userName = itemWithUser?.user?.profile?.name || "Unknown";

  return (
    <div className="inline-flex items-center gap-1 group">
      <Link
        to={`/users-management/users/${id}`}
        className="text-sm text-primary-600 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {userName}
      </Link>
      <CopyIconButton
        copyContent={id}
        size="xs"
        variant="ghost"
        className="opacity-0 group-hover:opacity-100"
      />
    </div>
  );
};

const renderUuid = ({ value }: FieldRendererProps): ReactNode => {
  const id = String(value ?? "");
  if (!id || id === "null" || id === "undefined") return "-";

  return <UUID value={id} />;
};

const renderDuration = ({ value }: FieldRendererProps): ReactNode => {
  const ms = Number(value);
  if (isNaN(ms)) return "-";

  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(2)}s`;
  }
  return `${ms}ms`;
};

const renderEmail = ({ value }: FieldRendererProps): ReactNode => {
  const email = String(value ?? "");
  if (!email || email === "null" || email === "undefined") return "-";

  return (
    <div className="inline-flex items-center gap-1 group">
      <Text as="span" size="sm">
        {email}
      </Text>
      <CopyIconButton
        copyContent={email}
        size="xs"
        variant="ghost"
        className="opacity-0 group-hover:opacity-100"
      />
    </div>
  );
};

const renderCompany = ({ value, item }: FieldRendererProps): ReactNode => {
  const id = String(value ?? "");
  if (!id || id === "null" || id === "undefined") return "-";

  const itemWithCompany = item as {
    company?: { id: string; name?: string };
  };
  const companyName = itemWithCompany?.company?.name || "Unknown";

  return (
    <div className="inline-flex items-center gap-1 group">
      <Link
        to={`/companies/${id}/dashboard/overview`}
        className="text-sm text-primary-600 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {companyName}
      </Link>
      <CopyIconButton
        copyContent={id}
        size="xs"
        variant="ghost"
        className="opacity-0 group-hover:opacity-100"
      />
    </div>
  );
};

const renderBoolean = ({ value }: FieldRendererProps): ReactNode => {
  if (value === null || value === undefined) return "-";
  return value ? (
    <Tag color="success" label="Yes" />
  ) : (
    <Tag color="neutral" label="No" />
  );
};

const fieldRenderers: Record<
  FieldType,
  (props: FieldRendererProps) => ReactNode
> = {
  [FieldType.String]: renderString,
  [FieldType.Uuid]: renderUuid,
  [FieldType.Email]: renderEmail,
  [FieldType.DateTime]: renderDateTime,
  [FieldType.Duration]: renderDuration,
  [FieldType.Enum]: renderEnum,
  [FieldType.JSON]: renderJSON,
  [FieldType.User]: renderUser,
  [FieldType.Company]: renderCompany,
  [FieldType.Boolean]: renderBoolean,
};

export const renderField = (
  type: FieldType,
  value: unknown,
  item: unknown,
  enumValues?: Record<string, { label: string; color?: string }>,
): ReactNode => {
  return fieldRenderers[type]({ value, item, enumValues });
};
